import {
  getLiquorCatalog,
  getLiqueurCatalog,
  getCrownCatalog,
  getSodaCatalog,
  getSyrupCatalog,
  getBeerCatalog,
  getWineCatalog,
  getChampagneCatalog,
  getSeltzerCatalog
} from '../services/catalogService'; // adjust path if needed
import { detectItemKind, parseUnitLabel, toGrams } from './measurement';

// Enhanced ingredient to catalog mapping with all beverage categories
const ingredientToCatalog = {
  "vodka": "liquor",
  "rum": "liquor",
  "tequila": "liquor",
  "gin": "liquor",
  "whiskey": "liquor",
  "bourbon": "liquor",
  "cognac": "liquor",
  "brandy": "liquor",
  "mezcal": "liquor",
  "absinthe": "liquor",
  "triple sec": "liqueur",
  "passionfruit": "liqueur",
  "peach": "liqueur",
  "lychee": "liqueur",
  "raspberry": "liqueur",
  "sour apple": "liqueur",
  "cointreau": "liqueur",
  "grand marnier": "liqueur",
  "kahlua": "liqueur",
  "baileys": "liqueur",
  "lime juice": "juice",
  "lemon juice": "juice",
  "orange juice": "juice",
  "pineapple juice": "juice",
  "grapefruit juice": "juice",
  "cranberry juice": "juice",
  "apple juice": "juice",
  "tomato juice": "juice",
  "grenadine": "syrup",
  "coconut": "syrup",
  "passionfruit syrup": "syrup",
  "simple syrup": "syrup",
  "honey syrup": "syrup",
  "agave syrup": "syrup",
  "club soda": "soda",
  "tonic water": "soda",
  "sprite": "soda",
  "coca-cola": "soda",
  "ginger beer": "soda",
  "ginger ale": "soda",
  "beer": "beer",
  "lager": "beer",
  "ipa": "beer",
  "pilsner": "beer",
  "stout": "beer",
  "wine": "wine",
  "chardonnay": "wine",
  "sauvignon blanc": "wine",
  "pinot noir": "wine",
  "cabernet": "wine",
  "merlot": "wine",
  "champagne": "champagne",
  "prosecco": "champagne",
  "cava": "champagne",
  "sparkling wine": "champagne",
  "seltzer": "seltzer",
  "sparkling water": "seltzer",
  "la croix": "seltzer",
  "perrier": "seltzer",
  "nuts": "snacks",
  "chips": "snacks",
  "olives": "snacks",
  "cheese": "snacks",
  "crackers": "snacks"
};

const getCatalogFetcher = (category) => {
  const fetchers = {
    liquor: getLiquorCatalog,
    liqueur: getLiqueurCatalog,
  juice: getCrownCatalog,
    soda: getSodaCatalog,
    syrup: getSyrupCatalog,
    beer: getBeerCatalog,
    wine: getWineCatalog,
    champagne: getChampagneCatalog,
    seltzer: getSeltzerCatalog
  };
  return fetchers[category];
};

export async function findProductForIngredient(ingredientName) {  
  const catalogType = ingredientToCatalog[ingredientName.toLowerCase()];
  
  if (!catalogType) {
    return null;
  }

  // Handle snacks category which doesn't have a catalog service yet
  if (catalogType === 'snacks') {
    return null;
  }
  const fetchCatalog = getCatalogFetcher(catalogType);
  if (!fetchCatalog) {
    return null;
  }

  const catalog = await fetchCatalog();

  const foundProduct = catalog.find(item =>
    item.name.toLowerCase().includes(ingredientName.toLowerCase()) ||
    (item.type && item.type.toLowerCase().includes(ingredientName.toLowerCase())) ||
    (item.flavor && item.flavor.toLowerCase().includes(ingredientName.toLowerCase()))
  );
  
  return foundProduct || null;
}

// New lightweight mapper used by pricing util
export function mapIngredientsToCatalog(ingredients = [], catalogs = {}, { storeId } = {}) {
  // Build a flexible lookup index from catalogs that may be a flat map or category arrays
  const index = {};
  if (catalogs && typeof catalogs === 'object') {
    // If catalogs looks like a flat id->entry map, copy directly
    const values = Object.values(catalogs);
    const looksFlat = values.every(v => v && typeof v === 'object' && !Array.isArray(v) && (v.id || v.sku || v.volume_ml || v.price != null));
    if (looksFlat) {
      Object.keys(catalogs).forEach(k => {
        const entry = catalogs[k];
        if (!entry) return;
        if (entry.id) index[entry.id] = entry;
        if (entry.sku) index[entry.sku] = entry;
        index[k] = entry;
        if (entry.name) {
          const key = String(entry.name).toLowerCase().replace(/[^a-z0-9]+/g, '_');
          if (!index[key]) index[key] = entry;
        }
      });
    } else {
      // Iterate category arrays
      Object.keys(catalogs).forEach(cat => {
        const arr = catalogs[cat];
        if (Array.isArray(arr)) {
          arr.forEach(entry => {
            if (!entry) return;
            if (entry.id) index[entry.id] = entry;
            if (entry.sku) index[entry.sku] = entry;
            if (entry.catalogSku) index[entry.catalogSku] = entry;
            if (entry.name) {
              const key = String(entry.name).toLowerCase().replace(/[^a-z0-9]+/g, '_');
              if (!index[key]) index[key] = entry;
            }
          });
        }
      });
    }
  }

  return (ingredients || []).map(ing => {
    const name = ing?.name || ing?.customIngredient?.name || '';
    const amountPerDrinkOz = parseFloat(ing.amountPerCocktailOz || ing.amountPerDrinkOz || ing.qty || 0) || 0;
    const mlPerOz = 29.5735;
  const unit = ing.unit || 'oz';

    // If explicitly custom per-drink cost
    if (ing.customIngredient?.costPerDrink || ing.costPerDrink) {
      return {
        custom: true,
        costPerDrink: parseFloat(ing.customIngredient?.costPerDrink || ing.costPerDrink || 0) || 0,
        amountPerDrinkOz,
        mlPerOz,
    unit,
      };
    }

    // Resolve catalog entry
    const catalogId = ing.catalogId || ing.catalogSku || ing.sku || null;
    let entry = catalogId && index[catalogId] ? index[catalogId] : null;
    if (!entry && (ing.name || ing.customIngredient?.name)) {
      const key = String(ing.name || ing.customIngredient?.name)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_');
      entry = index[key] || null;
    }

    // Determine kind and capacities
    const nameLower = String(name).toLowerCase();
    let kindInfo = entry ? detectItemKind(entry) : null;
    if (!kindInfo || kindInfo.kind === 'unknown') {
      // Try unitLabel from ingredient or custom
      const unitLabel = ing.unitLabel || ing.customIngredient?.unitLabel || null;
      if (unitLabel) {
        kindInfo = parseUnitLabel(unitLabel);
      } else {
        // Infer from wet/dry flags and name; default to liquid if wet/unknown
        const wetUnknown = (ing.customIngredient?.isWet !== false && ing.isWet !== false) ||
                           (ing.isWet === undefined && ing.customIngredient?.isWet === undefined);
        if (wetUnknown) {
          const defaultMl = nameLower.includes('syrup') ? 1000 : 750; // syrup defaults to 1L
          const cap = parseFloat(ing.customIngredient?.volume_ml || ing.bottleSizeMl || defaultMl) || defaultMl;
          kindInfo = { kind: 'liquid', capacityMl: cap };
        } else {
          // Treat as single-unit pack by default
          kindInfo = { kind: 'unit', capacityUnits: 1 };
        }
      }
    }
    const isLiquid = kindInfo.kind === 'liquid';
    const isUnit = kindInfo.kind === 'unit';
    const isWeight = kindInfo.kind === 'weight';

    // Bottle size and price fallbacks
  const bottleSizeMl = isLiquid ? (
      parseFloat(
        entry?.volume_ml || entry?.bottleSizeMl || ing.customIngredient?.volume_ml || ing.bottleSizeMl || kindInfo.capacityMl || 0
      ) || 0
    ) : 0;
    const capacityUnits = isUnit ? (parseFloat(kindInfo.capacityUnits || 1) || 1) : 0;
    const capacityGrams = isWeight ? (parseFloat(kindInfo.capacityGrams || 0) || 0) : 0;
  let rawPrice = entry?.price ?? entry?.pricePerBottle ?? entry?.pricePerUnit ?? ing.customIngredient?.price ?? ing.customIngredient?.pricePerUnit ?? ing.pricePerUnit ?? ing.price;
    if (typeof rawPrice === 'string') rawPrice = rawPrice.replace(/\$/g, '').trim();
    const pricePerBottle = Number.isFinite(parseFloat(rawPrice)) ? parseFloat(rawPrice) : 0;

    // Compute alternative per-drink amounts for non-liquids
    const amountUnits = isUnit ? (parseFloat(ing.qty || 0) || 0) : 0;
    const amountGrams = isWeight ? (toGrams(parseFloat(ing.qty || 0) || 0, unit) || 0) : 0;

    return {
      name,
      amountPerDrinkOz,
      mlPerOz,
      bottleSizeMl,
      pricePerBottle,
      unit,
      kind: kindInfo.kind,
      capacityUnits,
      capacityGrams,
      amountUnits,
      amountGrams,
      custom: false,
      costPerDrink: 0,
    };
  });
}
